import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { UploadCloud, Search, FileText, Activity } from "lucide-react";
import { Button } from "../ui/button";

export default function DashboardPreview() {
  return (
    <section id="preview" className="py-24 md:py-32 bg-white relative overflow-hidden">
      <div className="absolute inset-0 soft-gradient opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            <span className="font-mono-num">05</span> · Doctor Dashboard
          </div>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-light text-slate-900">
            A workspace that <br />
            <span className="text-slate-400">stays out of the way.</span>
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-14 glass rounded-3xl p-4 md:p-6 ring-1 ring-white/40 shadow-[0_40px_100px_-40px_rgba(15,23,42,0.35)]"
          data-testid="dashboard-preview"
        >
          <div className="grid lg:grid-cols-4 gap-4">
            {/* Sidebar */}
            <div className="rounded-2xl bg-white/80 backdrop-blur p-4 ring-1 ring-slate-200/60">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono-num text-slate-400 mb-3">
                Navigation
              </div>
              {[
                { icon: Activity, label: "Overview", active: true },
                { icon: UploadCloud, label: "New Scan" },
                { icon: Search, label: "Patients" },
                { icon: FileText, label: "Reports" },
              ].map((it) => {
                const Icon = it.icon;
                return (
                  <button
                    key={it.label}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all cursor-pointer w-full ${
                      it.active
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                    onClick={() => {}}
                  >
                    <Icon className="h-4 w-4" />
                    {it.label}
                  </button>
                );
              })}
              <div className="mt-6 p-3 rounded-lg bg-gradient-to-br from-sky-100 to-emerald-100">
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono-num text-slate-600">Today</div>
                <div className="font-heading text-2xl text-slate-900 mt-1">12 scans</div>
                <div className="text-xs text-slate-600">3 flagged · 9 benign</div>
              </div>
            </div>

            {/* Main */}
            <div className="lg:col-span-3 rounded-2xl bg-white/80 backdrop-blur p-6 ring-1 ring-slate-200/60">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] font-mono-num text-slate-400">Case #2309</div>
                  <div className="font-heading text-xl text-slate-900 mt-1">Patient · 34F · Lesion on forearm</div>
                </div>
                <div className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-mono-num tracking-wider px-2.5 py-1">
                  COMPLETE
                </div>
              </div>

              <div className="mt-6 grid md:grid-cols-3 gap-4">
                <div className="md:col-span-1 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 aspect-square ring-1 ring-slate-200 flex items-center justify-center">
                  <div className="text-center text-slate-400">
                    <Search className="h-8 w-8 mx-auto" />
                    <div className="text-[10px] mt-2 uppercase tracking-[0.25em] font-mono-num">Lesion preview</div>
                  </div>
                </div>
                <div className="md:col-span-2 space-y-3">
                  <div>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Melanocytic Nevus</span><span className="font-mono-num">94.7%</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full w-[94%] bg-gradient-to-r from-sky-400 to-emerald-400" />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Seborrheic Keratosis</span><span className="font-mono-num">3.1%</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full w-[3%] bg-slate-300" />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Basal Cell Carcinoma</span><span className="font-mono-num">1.4%</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full w-[1%] bg-slate-300" />
                    </div>
                  </div>
                  <div className="pt-3 border-t border-slate-100">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-400 mb-1.5">Agent trace</div>
                    <div className="flex flex-wrap gap-1.5">
                      {["Vision ✓", "Classifier ✓", "Validator ✓", "Safety ✓"].map((t) => (
                        <span key={t} className="text-[10px] font-mono-num bg-slate-900 text-sky-200 px-2 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-2">
                <Link to="/dashboard">
                  <Button className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="preview-open-dashboard-btn">
                    Open Dashboard
                  </Button>
                </Link>
                <Button variant="outline" className="rounded-full border-slate-300">Export PDF</Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
